import apiClient from './client';

import type { CanvasAssetType, CanvasContextType } from '@/types/canvas';
import type {
  CanvasCustomGroupLayoutMode,
  CanvasCustomGroupState,
} from '@/lib/canvas/canvas-custom-group';
import {
  canvasCustomGroupLayoutModeFromValue,
  canvasCustomGroupLayoutModeToValue,
} from '@/lib/canvas/canvas-custom-group';

export interface CanvasCustomGroupsListParams {
  projectId: string;
  contextType: CanvasContextType;
  assetType?: CanvasAssetType | null;
  assetId?: string | null;
  storyboardId?: string | null;
}

export interface CanvasCustomGroupsReplacePayload extends CanvasCustomGroupsListParams {
  groups: CanvasCustomGroupState[];
}

export interface CanvasCustomGroupsResponse {
  projectId: string;
  contextType: CanvasContextType;
  assetType?: CanvasAssetType | null;
  assetId?: string | null;
  storyboardId?: string | null;
  groups: CanvasCustomGroupState[];
}

const CONTEXT_TYPE_API_VALUE: Record<CanvasContextType, number> = {
  asset: 1,
  storyboard: 2,
};

const ASSET_TYPE_API_VALUE: Record<CanvasAssetType, number> = {
  character: 1,
  location: 2,
  prop: 3,
};

function readValue(record: Record<string, unknown>, camelKey: string, snakeKey: string) {
  return record[camelKey] ?? record[snakeKey];
}

function normalizePosition(
  value: unknown
): CanvasCustomGroupState['memberPositions'][string] | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const x = record.x;
  const y = record.y;
  if (typeof x !== 'number' || typeof y !== 'number') return null;
  const zIndex = record.zIndex ?? record.z_index;
  return typeof zIndex === 'number' ? { x, y, zIndex } : { x, y };
}

export function normalizeCanvasCustomGroup(value: unknown): CanvasCustomGroupState | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const groupId = readValue(record, 'canvasCustomGroupId', 'canvas_custom_group_id');
  if (typeof groupId !== 'string' || !groupId) return null;
  const rawMode = readValue(record, 'layoutMode', 'layout_mode');
  const layoutMode: CanvasCustomGroupLayoutMode =
    rawMode === 'horizontal' || rawMode === 'vertical' || rawMode === 'grid'
      ? rawMode
      : canvasCustomGroupLayoutModeFromValue(typeof rawMode === 'number' ? rawMode : 1);
  const gridColumns = readValue(record, 'gridColumns', 'grid_columns');
  const memberKeys = readValue(record, 'memberKeys', 'member_keys');
  const memberPositions = readValue(record, 'memberPositions', 'member_positions');
  if (typeof gridColumns !== 'number' || !Array.isArray(memberKeys) || !memberPositions) {
    return null;
  }
  const keys = memberKeys.filter((key): key is string => typeof key === 'string' && key.length > 0);
  if (keys.length < 2) return null;
  const positions: CanvasCustomGroupState['memberPositions'] = {};
  for (const [key, position] of Object.entries(memberPositions as Record<string, unknown>)) {
    const normalized = normalizePosition(position);
    if (normalized) positions[key] = normalized;
  }
  return {
    groupId,
    layoutMode,
    gridColumns,
    memberKeys: keys,
    memberPositions: positions,
  };
}

function serializeGroups(groups: CanvasCustomGroupState[]) {
  return groups.map((group) => ({
    canvasCustomGroupId: group.groupId,
    layoutMode: canvasCustomGroupLayoutModeToValue(group.layoutMode),
    gridColumns: group.gridColumns,
    memberKeys: group.memberKeys,
    memberPositions: group.memberPositions,
  }));
}

export const canvasCustomGroupsApi = {
  async list(params: CanvasCustomGroupsListParams): Promise<CanvasCustomGroupsResponse> {
    const response = (await apiClient.get(`/projects/${params.projectId}/canvas/custom-groups`, {
      params: {
        contextType: params.contextType,
        assetType: params.assetType ?? undefined,
        assetId: params.assetId ?? undefined,
        storyboardId: params.storyboardId ?? undefined,
      },
    })) as unknown as Record<string, unknown>;
    const groupsRaw = (response.groups ?? []) as unknown[];
    return {
      projectId: String(response.projectId ?? params.projectId),
      contextType: params.contextType,
      assetType: params.assetType,
      assetId: params.assetId,
      storyboardId: params.storyboardId,
      groups: groupsRaw
        .map((item) => normalizeCanvasCustomGroup(item))
        .filter((item): item is CanvasCustomGroupState => Boolean(item)),
    };
  },

  async replace(payload: CanvasCustomGroupsReplacePayload): Promise<CanvasCustomGroupsResponse> {
    const response = (await apiClient.put(`/projects/${payload.projectId}/canvas/custom-groups`, {
      contextType: CONTEXT_TYPE_API_VALUE[payload.contextType],
      assetType:
        payload.assetType == null ? payload.assetType : ASSET_TYPE_API_VALUE[payload.assetType],
      assetId: payload.assetId,
      storyboardId: payload.storyboardId,
      groups: serializeGroups(payload.groups),
    })) as unknown as Record<string, unknown>;
    const groupsRaw = (response.groups ?? []) as unknown[];
    return {
      projectId: String(response.projectId ?? payload.projectId),
      contextType: payload.contextType,
      assetType: payload.assetType,
      assetId: payload.assetId,
      storyboardId: payload.storyboardId,
      groups: groupsRaw
        .map((item) => normalizeCanvasCustomGroup(item))
        .filter((item): item is CanvasCustomGroupState => Boolean(item)),
    };
  },
};
